import { useState, useEffect } from 'react';
import { Music2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useAuthor } from '@/hooks/useAuthor';
import { useToast } from '@/hooks/useToast';
import { genUserName } from '@/lib/genUserName';
import type { MusicTrack } from '@/lib/musicTypes';

interface ShareDialogProps {
  track: MusicTrack;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ShareDialog({ track, open, onOpenChange }: ShareDialogProps) {
  const [comment, setComment] = useState('🎵 Check out this track');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate: createEvent } = useNostrPublish();
  const { toast } = useToast();

  // Reset to default message when dialog opens
  useEffect(() => {
    if (open) {
      setComment('🎵 Check out this track');
    }
  }, [open]);

  // Get artist info for preview
  const artistPubkey = track.artistPubkey || track.pubkey;
  const artist = useAuthor(artistPubkey);
  const artistMetadata = artist.data?.metadata;
  const artistName = track.artistName || artistMetadata?.name || genUserName(artistPubkey);
  const artistAvatar = artistMetadata?.picture;

  // Create naddr for the track
  const naddr = nip19.naddrEncode({
    kind: track.event.kind,
    pubkey: track.pubkey,
    identifier: track.id,
  });

  const handlePost = () => {
    if (!comment.trim()) {
      toast({
        title: 'Message Required',
        description: 'Please add a message to share this track',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    // Create a fresh kind 1 note that quotes the track
    // Include the naddr in the content and add a 'q' tag referencing the quoted event
    const content = `${comment.trim()}\n\nnostr:${naddr}`;

    createEvent(
      {
        kind: 1,
        content,
        tags: [
          ['q', track.event.id],
        ],
      },
      {
        onSuccess: () => {
          toast({
            title: 'Track Shared',
            description: 'Your share has been posted',
          });
          onOpenChange(false);
        },
        onError: (error) => {
          toast({
            title: 'Share Failed',
            description: error instanceof Error ? error.message : 'Failed to share track',
            variant: 'destructive',
          });
        },
        onSettled: () => {
          setIsSubmitting(false);
        },
      }
    );
  };

  const backgroundStyle = track.imageUrl
    ? { backgroundImage: `url(${track.imageUrl})` }
    : track.gradient
    ? { background: `linear-gradient(135deg, ${track.gradient.join(', ')})` }
    : { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Share Track</DialogTitle>
          <DialogDescription>
            Add your message about this track
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Comment Input */}
          <Textarea
            placeholder="🎵 Check out this track"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="min-h-[100px] resize-none"
            disabled={isSubmitting}
          />

          {/* Track Preview */}
          <div className="rounded-lg border border-border/50 p-3 bg-muted/30">
            <div className="flex items-center gap-3">
              {/* Album Art */}
              <div
                className="w-16 h-16 rounded-lg bg-cover bg-center flex-shrink-0"
                style={backgroundStyle}
              >
                {!track.imageUrl && (
                  <div className="w-full h-full flex items-center justify-center bg-black/20 rounded-lg">
                    <Music2 className="h-8 w-8 text-white/80" />
                  </div>
                )}
              </div>

              {/* Track Info */}
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm line-clamp-1">
                  {track.title}
                </h4>
                <div className="flex items-center gap-2 mt-1">
                  {track.artistPubkey && (
                    <Link
                      to={`/${nip19.npubEncode(artistPubkey)}`}
                      className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Avatar className="h-5 w-5">
                        <AvatarImage src={artistAvatar} alt={artistName} />
                        <AvatarFallback className="text-[10px]">
                          {artistName.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-xs text-muted-foreground line-clamp-1">
                        {artistName}
                      </span>
                    </Link>
                  )}
                  {!track.artistPubkey && (
                    <span className="text-xs text-muted-foreground line-clamp-1">
                      {artistName}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePost}
              disabled={isSubmitting || !comment.trim()}
            >
              {isSubmitting ? 'Posting...' : 'Post'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
